import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Switch, Route } from 'react-router'

import { motion, AnimatePresence, AnimatePresenceProps } from 'framer-motion'

import { Helmet } from 'react-helmet-async'
import AuthenticatedView from '@/authenticated-view'
import { toUnit } from '@/utils/styles'
import { colors, breakpoints, structure } from '@/styles'
import { ProductsProvider } from './context/products'

// Components
import MyProducts from './components/MyProducts'
import AllProducts from './components/AllProducts'
import LeasesAndPayments from './components/LeasesAndPayments'
import RentalAdvertising from './components/RentalAdvertising'
import TenantScreening from './components/TenantScreening'

const Wrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: ${colors.white};
  min-height: calc(100vh - ${toUnit(structure.header.height)} - 50px);
  height: 100%;

  background-repeat: no-repeat;
  background-position: -43px 63px, 98% 55px, 100% 92%;
  background-size: 168px, 230px, 400px;

  @media screen and (max-width: ${breakpoints.dashboardMobile}) {
    background-repeat: no-repeat;
    background-position: -43px 63px, 98% 55px, 100% 98%;
    background-size: 168px, 230px, 250px;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    background-position: -70px 65px, 100% 70px, 100% 98%;
    background-size: 150px, 100px, 250px;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    min-height: calc(100vh - ${toUnit(structure.header.heightMobile)} - 50px);

    background-position: 100% 50px, 100% 98%;
    background-size: 100px, 170px;
  }
`

interface NewAnimatePresenceProps extends Omit<AnimatePresenceProps, 'children'> {
  children: React.ReactNode
}

const AnimatedComponent = ({ children }: { children: React.ReactNode }) => {
  const NewAnimatePresence: React.FC<NewAnimatePresenceProps> = AnimatePresence

  return <NewAnimatePresence mode="wait">{children}</NewAnimatePresence>
}

const UsageCredits = ({ sidebarExtended }: any) => {
  const { t } = useTranslation()

  return (
    <>
      <Helmet>
        <title>{t('b.usage.credits.title', 'Credit Usage')}</title>
      </Helmet>
      <ProductsProvider>
        <AnimatedComponent>
          <Wrapper>
            <Switch>
              <AuthenticatedView
                path="/usage-credits/products/my-products"
                render={(props: any) => <MyProducts sidebarExtended={sidebarExtended} {...props} />}
              />
              <AuthenticatedView
                path="/usage-credits/products/all"
                render={(props: any) => (
                  <AllProducts sidebarExtended={sidebarExtended} {...props} />
                )}
              />
              <AuthenticatedView
                path="/usage-credits/products/tenant-screening"
                render={(props: any) => (
                  <TenantScreening sidebarExtended={sidebarExtended} {...props} />
                )}
              />
              <AuthenticatedView
                path="/usage-credits/products/rental-advertising"
                render={(props: any) => (
                  <RentalAdvertising sidebarExtended={sidebarExtended} {...props} />
                )}
              />
              <AuthenticatedView
                path="/usage-credits/products/lease-payments"
                render={(props: any) => (
                  <LeasesAndPayments sidebarExtended={sidebarExtended} {...props} />
                )}
              />
            </Switch>
          </Wrapper>
        </AnimatedComponent>
      </ProductsProvider>
    </>
  )
}

export default UsageCredits
